import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControl,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { typographyStyles } from "constants/styles";
import { formatDateOnlyDMY, toDateValue } from "helpers";
import { useCallback, useState, useMemo } from "react";
import {
  IIncomeEntryArticleBundle,
  IIncomeEntryArticleBundleObservation,
  SubLocation,
  Location,
} from "types";
import IncomeEntryArticleBundleObservationCard from "./IncomeEntryArticleBundleObservationCard";
import { useSession } from "providers/SessionProvider";
import { DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";

const inputSx = {
  "& .MuiInputLabel-root": {
    zIndex: 0,
  },
  "& .MuiInputBase-root": {
    zIndex: 0,
  },
};

function colorState(arrived: boolean, hasObservations: boolean) {
  if (!arrived) return "divider";
  if (hasObservations) return "warning.main";
  return "success.main";
}

const parseAmount = (text: string): number | null => {
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
};

const parseWeight = (text: string): number | null => {
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
};

interface IncomeEntryArticleBundleProps {
  bundle: IIncomeEntryArticleBundle;
  weightKgPerItem?: string | null;
  storeId: number | null;
  onSubmit: (bundle: IIncomeEntryArticleBundle) => void;
}

export default function IncomeEntryArticleBundleCard({
  bundle,
  weightKgPerItem,
  storeId,
  onSubmit,
}: IncomeEntryArticleBundleProps) {
  const { locations, sublocations } = useSession();

  const locationOptions = useMemo(() => {
    const locationList = storeId && storeId !== 0
      ? locations.filter(l => l.store_id === storeId)
      : locations;
    return locationList.map(({ id, code }) => ({ id, label: code }))
  }, [locations, storeId]);

  const [actualLocation, setActualLocation] = useState<Location | null>(() => {
    if(!bundle.location) return null;
    if(bundle.location.parent_location_id) return null;
    return bundle.location || null;
  });

  const sublocationOptions = useMemo(() => {
    const parentId = actualLocation?.id ?? null;
    return sublocations
      .filter(sl => sl.parent_location_id === parentId)
      .map(({ id, code }) => ({ id, label: code }));
  }, [sublocations, actualLocation?.id]);

  const [actualSublocation, setActualSublocation] = useState<SubLocation | null>(() => {
    if(!bundle.location?.parent_location_id) return null;
    return bundle.location as SubLocation || null;
  });

  const [articleBundle, setArticleBundle] =
    useState<IIncomeEntryArticleBundle>(bundle);
  const [dateInput, setDateInput] = useState<Date | null>(() =>
    toDateValue(bundle.fecha_elaboracion?.slice(0, 10)),
  );
  const [amountInput, setAmountInput] = useState(() =>
    !bundle.arrived && bundle.amount === 0 ? "" : String(bundle.amount),
  );
  const [weightInput, setWeightInput] = useState(() =>
    bundle.weight == null || (!bundle.arrived && bundle.weight === 0)
      ? ""
      : String(bundle.weight),
  );

  const [errors, setErrors] = useState<(keyof IIncomeEntryArticleBundle)[]>([]);

  const checkForm = useCallback((b: IIncomeEntryArticleBundle) => {
    const errors: (keyof IIncomeEntryArticleBundle)[] = [];
    if (!b.fecha_elaboracion || !dateInput || Number.isNaN(dateInput.getTime())) {
      errors.push("fecha_elaboracion");
    }
    if (!b.location_id) errors.push("location_id");
    if (parseWeight(weightInput) === null) errors.push("weight");
    if (parseAmount(amountInput) === null) errors.push("amount");
    return errors;
  }, [amountInput, dateInput, weightInput]);

  const handleDateChange = (newValue: Date | null) => {
    setDateInput(newValue);
    if (newValue === null) {
      handleChangeArticleBundle("fecha_elaboracion", null);
    } else if (!Number.isNaN(newValue.getTime())) {
      handleChangeArticleBundle("fecha_elaboracion", format(newValue, "yyyy-MM-dd"));
    }
  };

  const handleChangeArticleBundle = (
    field: keyof IIncomeEntryArticleBundle,
    value?: any,
    type?: "sublocation" | "location"
  ) => {
    setErrors((prev) => prev.filter((e) => e !== field));
    if (field === "id") return;
    if (field === "arrived") return;
    if(field === "location_id") {
      let nextLocation: Location | SubLocation | null;
      if(type === "sublocation") {
        nextLocation = sublocations.find(sl => sl.id === value?.id) ?? null;
      } else {
        nextLocation = locations.find(l => l.id === value?.id) ?? null;
      }
      setArticleBundle((prev) => {
        const nextBundle = {
          ...prev,
          location_id: value?.id ?? null,
          location: nextLocation,
        };
        onSubmit(nextBundle);
        if(type === "sublocation") {
          setActualSublocation(nextLocation as SubLocation ?? null);
        } else {
          setActualSublocation(null);
          setActualLocation(nextLocation ?? null);
        }
        return nextBundle;
      });
      return;
    };
    setArticleBundle((prev) => {
      const nextBundle = { ...prev, [field]: value };
      onSubmit(nextBundle);
      return nextBundle;
    });
  };

  const handleAmountChange = (text: string) => {
    setAmountInput(text);
    const amount = parseAmount(text);
    const weightPerItem = Number(weightKgPerItem);
    const suggestedWeight = amount !== null && weightKgPerItem &&
      Number.isFinite(weightPerItem) && weightPerItem > 0
      ? Number((amount * weightPerItem).toFixed(2))
      : null;
    setErrors((prev) => prev.filter((error) =>
      error !== "amount" && (suggestedWeight === null || error !== "weight"),
    ));
    if (suggestedWeight !== null) setWeightInput(String(suggestedWeight));
    setArticleBundle((prev) => {
      const nextBundle = {
        ...prev,
        amount: amount ?? 0,
        amount_available: amount ?? 0,
        weight: suggestedWeight ?? prev.weight,
        arrived: amount === null ? false : prev.arrived,
      };
      onSubmit(nextBundle);
      return nextBundle;
    });
  };

  const handleWeightChange = (text: string) => {
    setWeightInput(text);
    setErrors((prev) => prev.filter((error) => error !== "weight"));
    const weight = parseWeight(text);
    setArticleBundle((prev) => {
      const nextBundle = {
        ...prev,
        weight,
        arrived: weight === null ? false : prev.arrived,
      };
      onSubmit(nextBundle);
      return nextBundle;
    });
  };

  const handleToggleArrived = useCallback(() => {
    setArticleBundle((prev) => {
      const nextArrived = !prev.arrived;
      const nextBundle = {
        ...prev,
        location_id: actualSublocation?.id || actualLocation?.id || null,
        location: actualSublocation || actualLocation || null,
        arrived: nextArrived
      };

      if (nextArrived) {
        const formErrors = checkForm(nextBundle);
        setErrors(formErrors);
        if (formErrors.length > 0) {
          return prev;
        }
      }

      onSubmit(nextBundle);
      return nextBundle;
    });
  }, [actualLocation, actualSublocation, checkForm, onSubmit]);

  // observations handlers
  const [openObs, setOpenObs] = useState(false);
  const [observation, setObservation] = useState<
    IIncomeEntryArticleBundleObservation | undefined
  >(bundle.observations?.[0]);
  const handleSaveBundleObservation = (
    bundleId: number,
    observation: Omit<IIncomeEntryArticleBundleObservation, "id">,
  ) => {
    setObservation(observation as IIncomeEntryArticleBundleObservation);
    handleChangeArticleBundle("observations", [observation]);
  };
  const handleRemoveBundleObservation = () => {
    setObservation(undefined);
    handleChangeArticleBundle("observations", []);
  };

  const borderColor = colorState(
    Boolean(articleBundle.arrived),
    Boolean(observation),
  );

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderWidth: 1.5,
          borderColor: borderColor,
          borderRadius: 1,
          p: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{ alignItems: "end", justifyContent: "flex-start" }}
          onClick={handleToggleArrived}
        >
          {/* check */}
          <Checkbox
            checked={!!articleBundle.arrived}
            color="default"
            sx={{
              p: 1,
              "& .MuiSvgIcon-root": { fontSize: 32 },
              "&.Mui-checked": { color: borderColor },
            }}
          />
          {/* title */}
          <Stack direction="column">
            <Typography
              sx={{
                ...typographyStyles.monospace,
                fontWeight: 700,
                fontSize: 22,
                lineHeight: 1,
              }}
            >
              F-{articleBundle.lote_externo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fecha de elaboración {formatDateOnlyDMY(articleBundle.fecha_elaboracion ?? "")}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        {/* Data */}
        <Stack sx={{ p: 1 }}>
          {/* Fecha de elaboración */}
          <FormControl fullWidth sx={{m: 0}}>
            <DatePicker
              label="Fecha de elaboración"
              value={dateInput}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  error: errors.includes("fecha_elaboracion"),
                  required: true,
                }
              }}
            />
          </FormControl>

          {/* Cantidad - Peso */}
          <Stack direction="row" spacing={1}>
            <FormControl fullWidth>
              <TextField 
                type="number"
                inputProps={{ inputMode: "numeric" }}
                color="secondary"
                value={amountInput}
                onChange={(e) => handleAmountChange(e.target.value)}
                error={errors.includes("amount")}
                placeholder="Cantidad"
                label="Cantidad"
                sx={inputSx}
              />
            </FormControl>
            <FormControl fullWidth>
              <TextField
                type="number"
                inputProps={{ inputMode: "numeric" }}
                color="secondary"
                value={weightInput}
                onChange={(e) => handleWeightChange(e.target.value)}
                error={errors.includes("weight")}
                placeholder="Peso"
                label="Peso"
                helperText={weightKgPerItem ? "Peso sugerido; podés ajustarlo." : undefined}
                sx={inputSx}
              />
            </FormControl>
          </Stack>

          {/* Ubicación - Sububicacion */}
          <Stack direction="row" spacing={1}>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                autoComplete
                value={ actualLocation && {
                  id: actualLocation.id,
                  label: actualLocation.code
                }}
                options={locationOptions}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
                onChange={(event, newValue) => {
                  handleChangeArticleBundle("location_id", newValue ?? null, "location");
                }}
                renderInput={(params) => (
                  <TextField {...params} required label="Ubicación" error={errors.includes("location_id")}/>
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.label}
                  </li>
                )}
              />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                autoComplete
                value={
                  actualSublocation && {
                    id: actualSublocation.id,
                    label: actualSublocation?.code ?? ""
                  }
                }
                options={sublocationOptions}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
                onChange={(event, newValue) => {
                  handleChangeArticleBundle("location_id", newValue ?? null, "sublocation");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Sububicación"/>
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.label}
                  </li>
                )}
              />
            </FormControl>
          </Stack>

          {/* Observacion */}
          <Button
            variant="outlined"
            color="warning"
            size="medium"
            sx={{ mt: 1, mb: 1 }}
            onClick={() => setOpenObs(true)}
          >
            {observation ? "Editar observación" : "Agregar observación"}
          </Button>
        </Stack>
      </Card>

      <IncomeEntryArticleBundleObservationCard
        bundleId={bundle.id}
        observation={observation}
        open={openObs}
        onSave={handleSaveBundleObservation}
        onRemove={handleRemoveBundleObservation}
        onClose={() => setOpenObs(false)}
      />
    </>
  );
}
